import { Pencil } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox, CheckboxField } from '@/components/ui/catalyst/checkbox';
import { Field, Label } from '@/components/ui/catalyst/fieldset';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  type FirmRoleAssignmentDetailWithConfig,
  type UpdateFirmRoleAssignmentSchema,
  type UpdateFirmRoleAssignmentWithConfig,
  useUpdateFirmRoleAssignmentMutation,
} from '@/services/api/firmsService';
import { useGetStaffUsersQuery } from '@/services/api/userService';
import { getUserDisplayName } from '@/utils/user';

type Props = {
  firmId: string;
  assignment: FirmRoleAssignmentDetailWithConfig;
  onUpdated?: () => void;
};

export const EditRoleAssignmentDialog: React.FC<Props> = ({
  firmId,
  assignment,
  onUpdated,
}) => {
  const [open, setOpen] = useState(false);
  const [assigneeId, setAssigneeId] = useState<string>(
    assignment.assignee_id as string,
  );
  const [caseAutoAssign, setCaseAutoAssign] = useState(
    assignment.config?.case_auto_assign ?? false,
  );
  const isCaseManager = assignment.role === 'case_manager';

  const [updateRoleAssignment, { isLoading }] =
    useUpdateFirmRoleAssignmentMutation();

  const { data: users = [], isLoading: isLoadingUsers } =
    useGetStaffUsersQuery();

  const userOptions = useMemo(
    () =>
      users.map((user) => ({
        id: user.id,
        label: getUserDisplayName(user),
      })),
    [users],
  );

  const handleSave = async () => {
    if (!firmId || !assigneeId) {
      toast.error('Please select an assignee');
      return;
    }
    try {
      // Build the payload with config for case managers
      const payload: UpdateFirmRoleAssignmentWithConfig = {
        assignee_id: assigneeId,
      };

      // Add config for case manager role
      if (isCaseManager) {
        payload.config = {
          case_auto_assign: caseAutoAssign,
        };
      }

      await updateRoleAssignment({
        firmPk: firmId,
        id: assignment.id as string,
        patchedUpdateFirmRoleAssignmentSchema:
          payload as unknown as UpdateFirmRoleAssignmentSchema,
      }).unwrap();
      toast.success('Role assignment updated');
      setOpen(false);
      if (onUpdated) onUpdated();
    } catch (e: unknown) {
      const error = e as { data?: { detail?: string } };
      const msg = error?.data?.detail || 'Failed to update role assignment';
      toast.error(msg);
    }
  };

  const handleCancel = () => {
    setOpen(false);
    setAssigneeId(assignment.assignee_id as string);
    setCaseAutoAssign(assignment.config?.case_auto_assign ?? false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setAssigneeId(assignment.assignee_id as string);
      setCaseAutoAssign(assignment.config?.case_auto_assign ?? false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost">
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Role Assignment</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Field>
            <Label>Role</Label>
            <div className="text-sm text-gray-700">
              {assignment.role
                .split('_')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')}
            </div>
          </Field>
          <Field>
            <Label htmlFor="assignee_select_edit">Assignee</Label>
            <Select
              value={assigneeId}
              onValueChange={setAssigneeId}
              disabled={isLoadingUsers}
            >
              <SelectTrigger id="assignee_select_edit">
                <SelectValue
                  placeholder={
                    isLoadingUsers ? 'Loading users...' : 'Select a user'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {userOptions.length === 0 && !isLoadingUsers && (
                  <SelectItem value="__empty__" disabled>
                    No users found
                  </SelectItem>
                )}
                {userOptions.map((opt) => (
                  <SelectItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {/* Per-role setting - show right after assignee when case manager */}
          {isCaseManager && (
            <div>
              <CheckboxField>
                <Checkbox
                  checked={caseAutoAssign}
                  onChange={(checked) => setCaseAutoAssign(checked)}
                  color="indigo"
                />
                <Label>Auto-assign new matters to this person</Label>
              </CheckboxField>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 ml-7 mt-1">
                When enabled, this case manager will automatically receive new
                matters via round-robin distribution.
              </p>
            </div>
          )}

        </div>
        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading || !assigneeId}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
